// ============================================================
// TMR Portal Admin — as colunas da planilha de Contratos
// ============================================================
import { CONTRACT_STATUS_OPTIONS, documentDisplayName, signatureProviderLabel } from '../../data/contracts/status';
import type { ContractRecord, ContractStatus } from '../../data/contracts/contract';
import type { ExportColumn } from '../../lib/export/types';

/** Status → the words the badge on screen uses. Built from the filter's own options, never retyped. */
const STATUS_LABEL = Object.fromEntries(
  CONTRACT_STATUS_OPTIONS.map((option) => [option.value, option.label]),
) as Record<ContractStatus, string>;

/** How the request reached the signatory, in the operator's words. */
const CHANNEL_LABEL: Record<string, string> = {
  Email: 'E-mail',
  WhatsApp: 'WhatsApp',
  Sms: 'SMS',
};

/**
 * The Contracts list as a spreadsheet (20 §9).
 *
 * **It carries the Empresa's id, not its name**, and that is a gap rather than a choice: the contracts
 * list does not return the name — the screen resolves it per row with its own request, which works for
 * ten rows on a page and cannot work for a file of thousands. Registered in `20` §12; closing it means
 * the list itself carrying the name, which would also fix the N+1 the table does today.
 *
 * The three signature dates are here even though the table shows one: a spreadsheet is exactly where
 * somebody goes to ask how long signing took.
 */
export const CONTRACT_EXPORT_COLUMNS: ExportColumn<ContractRecord>[] = [
  { header: 'Empresa (id)', value: (c) => c.partyId },
  { header: 'Produtor no legado', value: (c) => c.legacyPartyId },
  { header: 'Situação', value: (c) => STATUS_LABEL[c.status] ?? c.status },
  { header: 'Documento', value: (c) => documentDisplayName(c.documentPath) ?? '' },
  { header: 'Provedor', value: (c) => signatureProviderLabel(c.signatureProvider) },
  {
    header: 'Canal do envio',
    value: (c) => (c.signatureDeliveryChannel ? CHANNEL_LABEL[c.signatureDeliveryChannel] ?? c.signatureDeliveryChannel : ''),
  },
  { header: 'Enviado para', value: (c) => c.signatureDeliveryTarget ?? '' },
  { header: 'Emitido em', value: (c) => new Date(c.createdAt) },
  { header: 'Enviado em', value: (c) => toDate(c.signatureRequestedAt) },
  { header: 'Assinado pela empresa em', value: (c) => toDate(c.partySignedAt) },
  { header: 'Assinado pela TMR em', value: (c) => toDate(c.tmrSignedAt) },
  { header: 'Cancelado em', value: (c) => toDate(c.cancelledAt) },
  { header: 'Motivo do cancelamento', value: (c) => c.cancellationReason ?? '' },
];

/** An optional instant as a real date, so the cell sorts — never as the string `null`. */
function toDate(instant: string | null): Date | null {
  return instant ? new Date(instant) : null;
}
